import React, { useEffect, useRef, useState } from 'react';
import { useImageLoaded } from '~/hooks/useImage';
import ChevronIcon from '../../fragments/chevron-icon';
import PopsLabel from '../../fragments/pops-label';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { graphql } from 'gatsby';

const Pricing: React.FC<PricingProps> = ({ block }) => {
  const { pricing_groups, pricing_footer } = block;
  const [activeGroup, setActiveGroup] = useState(
    pricing_groups[0]?.heading?.title ?? ''
  );

  const groupRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const titleToSlug = (title: string) =>
    title
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^\w-]+/g, '');

  // Set active group from hash if found
  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash) return;

    const match = pricing_groups.find(
      group => titleToSlug(group.heading?.title) === hash
    );

    if (match) {
      const matchedTitle = match.heading?.title;
      setActiveGroup(matchedTitle);

      setTimeout(() => {
        const node = groupRefs.current[matchedTitle];
        node?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [pricing_groups]);

  return (
    <div className={`block__pricing`}>
      <div className='pricing-groups'>
        {pricing_groups.map(({ heading, image, pricing_sections }) => (
          <div
            key={heading?.title}
            ref={el => {
              groupRefs.current[heading?.title || ''] = el;
            }}
          >
            <MemoizedPricingGroup
              heading={heading}
              image={image}
              pricing_sections={pricing_sections}
              showChevron={pricing_groups.length > 1}
              isActive={
                activeGroup === heading?.title || pricing_groups.length === 1
              }
              changeGroup={() => setActiveGroup(heading?.title || '')}
            />
          </div>
        ))}
      </div>
      <PricingFooter data={pricing_footer} />
    </div>
  );
};

const PricingFooter: React.FC<{ data?: PricingFooterProps }> = ({ data }) => {
  if (!data) {
    return null;
  }

  return (
    <div className={`pricing-footer`}>
      <p className='pricing-footer__text'>{data.text}</p>
      <PricingCTA {...data.cta} />
    </div>
  );
};

const PricingGroup: React.FC<PricingGroupProps> = ({
  heading,
  image,
  pricing_sections,
  showChevron,
  isActive,
  changeGroup,
}) => {
  const [iconRef, iconLoaded, iconOnLoad] = useImageLoaded();
  const [coverRef, coverLoaded, coverOnLoad] = useImageLoaded();
  const [parent] = useAutoAnimate<HTMLDivElement>({
    easing: 'ease-out',
  });

  function headingClickHandler() {
    if (!isActive) {
      changeGroup();
    }
  }

  return (
    <div
      ref={parent}
      className={`pricing-group ${isActive ? 'pricing-group--active' : ''}`}
    >
      {heading?.title ? (
        <div
          className={`pricing-heading`}
          onClick={headingClickHandler}
        >
          {heading.image && heading.image.url && (
            <img
              ref={iconRef}
              className={`pricing-heading__image ${iconLoaded ? 'loaded' : ''}`}
              src={heading.image.url}
              alt={heading.image.alt}
              onLoad={iconOnLoad}
              loading='lazy'
            />
          )}
          <div className='pricing-heading__title'>{heading.title}</div>

          {showChevron && <ChevronIcon opened={isActive} />}
        </div>
      ) : (
        <div className='m-0 h-0 p-0'>{/* Empty heading */}</div>
      )}
      {isActive && (
        <>
          <PricingSections
            isActive={isActive}
            pricing_sections={pricing_sections}
          />
          {image && (
            <img
              ref={coverRef}
              className={`pricing-cover ${coverLoaded ? 'loaded' : ''}`}
              src={image.url}
              alt={image.alt}
              style={{
                translate: `${image.offset?.horizontal ?? 0}rem ${image.offset?.vertical ?? 0}rem`,
                scale: `${image?.scale?.x ?? 1} ${image?.scale?.y ?? 1}`,
              }}
              onLoad={coverOnLoad}
              loading='lazy'
            />
          )}
        </>
      )}
    </div>
  );
};

const PricingSections: React.FC<PricingSectionsProps> = ({
  pricing_sections,
}) => {
  return (
    <div className='pricing-sections'>
      {pricing_sections.map(
        ({ title, pops, description, pricing_items, cta }) => (
          <div
            key={title}
            className={`pricing-section`}
          >
            <h4 className='pricing-section__title'>
              {title} <PopsLabel text={pops} />
            </h4>
            <p className='pricing-section__description'>{description}</p>
            <ul className='pricing-section__items'>
              {pricing_items?.map(pricing_item => (
                <li
                  key={pricing_item.label}
                  className={`pricing-item`}
                >
                  <span className='pricing-item__label'>
                    {pricing_item.label}
                  </span>
                  <div className='pricing-item__cost'>
                    <span>{pricing_item.cost}</span>
                    {pricing_item.description && (
                      <span className='pricing-item__description'>
                        <div className='pricing-item__description-icon'>?</div>
                        <div
                          className='pricing-item__description-text'
                          dangerouslySetInnerHTML={{
                            __html: pricing_item.description,
                          }}
                        ></div>
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
            <PricingCTA {...cta} />
          </div>
        )
      )}
    </div>
  );
};

const PricingCTA: React.FC<PricingCta> = ({ text, link }) => {
  if (!link) {
    return null;
  }

  return (
    <a
      className='pricing-section__cta cta cta--compact'
      href={link}
    >
      {text}
    </a>
  );
};

const MemoizedPricingGroup = React.memo(PricingGroup);

type PricingSectionsProps = {
  isActive: boolean;
  pricing_sections: PricingSection[];
};

type PricingSection = {
  title?: string;
  description?: string;
  pops: string;
  pricing_items: PricingItem[];
  cta: PricingCta;
};

type PricingItem = {
  label: string;
  cost: string;
  description?: string;
};

type PricingCta = {
  text: string;
  link: string;
};

type PricingImage = {
  url: string;
  alt: string;
};

type PricingGroupProps = {
  heading: PricingHeading;
  image: PricingImage & Offset & Scale;
  pricing_sections: PricingSection[];
  // Component fields
  isActive: boolean;
  showChevron: boolean;
  changeGroup: () => void;
};

type PricingHeading = {
  title: string;
  image: PricingImage;
};

type PricingFooterProps = {
  text: string;
  cta: {
    text: string;
    link: string;
  };
};

type PricingBlockProps = {
  pricing_groups: PricingGroupProps[];
  pricing_footer?: PricingFooterProps;
};

type PricingProps = {
  block: PricingBlockProps;
};

export default Pricing;

export const pricingFragment = graphql`
  fragment PricingFragment on Block {
    pricing_groups {
      heading {
        title
        image {
          url
          alt
        }
      }
      image {
        url
        alt
        offset {
          horizontal
          vertical
        }
        scale {
          x
          y
        }
      }
      pricing_sections {
        title
        pops
        description
        pricing_items {
          label
          cost
          description
        }
        cta {
          text
          link
        }
      }
    }
    pricing_footer {
      text
      cta {
        text
        link
      }
    }
  }
`;
